import type { DataPoints } from '@retracedgmbh/data-points-lib';
import DataPointsLib from '@retracedgmbh/data-points-lib';
import { NotFoundError } from '../../../errors.js';
import type { DbClient } from '../../../lib/db.js';
import {
  COMPANY_CORE_INFO_TEMPLATES,
  COMPANY_CORE_INFO_TEMPLATES_ENUMS,
  COMPANY_PROFILE,
  CONNECTION_TYPES_PAIR,
  extractUserAuditMetaFromCtx,
  RELATIONAL_DATA_POINTS_TO_COPY,
  SUPPLIER_QUESTIONNAIRE_COMPANY_PROFILE_CREATE,
} from '../../constants.js';
import { upsertAssetAccessDb } from '../db/asset-access/upsert.js';
import { getCompanyByIdDb } from '../db/companies/get-by-id.js';
import { saveCompanyDataPointsAndTemplateAnswersDb } from '../db/companies/save-data-points-and-template-answers.js';
import { hasAnyConnectionDb } from '../db/company-links/has-any-connection.js';
import {
  type CompanyProfileRow,
  getCompanyAboutProfileDb,
} from '../db/company-profiles/get-about-profile.js';
import { upsertCompanyProfileDb } from '../db/company-profiles/upsert.js';
import { createAssetDataPointAnswerWithTemplateDb } from '../db/data-point-answers/create-asset-with-template.js';
import { getDataPointAnswersByDataPointsDb } from '../db/data-point-answers/get-by-data-points.js';
import { listPublicAssetAnswersByCompanyIdDb } from '../db/data-point-answers/list-public-by-company.js';
import type { DataPointAnswerRow } from '../db/data-point-answers/types.js';
import { getTemplateByIdDb } from '../db/templates/get-by-id.js';
import { listTemplatesUnscopedDb } from '../db/templates/list-unscoped.js';
import type { Template } from '../db/templates/types.js';

const ABOUT_TEMPLATE_ID = COMPANY_CORE_INFO_TEMPLATES.ABOUT;
const RIGHTS_READ = ['READ'];
const ACCESS_STATUS_ACTIVE = 'ACTIVE';

type DataPointValue = { value: unknown };
type TemplateAnswer = Record<string, DataPointValue>;
type TemplateAnswers = Record<string, TemplateAnswer>;
type AuditData = Record<string, unknown>;

export type CompanyIds = {
  forCompanyId: string;
  byCompanyId: string;
  onBehalfOfCompanyId: string;
};

export type CompanyLinkServiceDeps = {
  db: DbClient;
  publish: (event: string, data: Record<string, unknown>) => Promise<void>;
};

export type CompanyLinkSingleParams = {
  forCompanyId: string;
  byCompanyId: string;
  connectionType: string;
  auditData: AuditData;
  /**
   * Legacy `handler.js` only published `COMPANY_PROFILE.LINK` when
   * `byCompanyId === ctx.companyId` — true for every call from the `/link`
   * route (byCompanyId is always the caller's own company there), but NOT
   * true for the manual "link the other direction too" calls made by
   * `api-service`'s `supplychains.js`/`supplier-network.js`, which pass an
   * arbitrary `byCompanyId`. Those callers must pass `notifyLink: false` for
   * the direction that doesn't match their own `ctx.companyId`. Defaults to
   * `true` since every other caller satisfies the invariant.
   */
  notifyLink?: boolean;
};

export type CompanyLinkBulkParams = {
  forCompanyIds: string[];
  byCompanyId: string;
  connectionType: string;
  auditData: AuditData;
};

/**
 * Ports the legacy `POST companies-service/v1/companies/link` write path:
 * links two companies bidirectionally (a forward profile using the request's
 * `connectionType`, plus a reverse profile using its pair), auto-shares the
 * caller's public AUDITS assets with the newly-linked company, and publishes
 * the same legacy queue events the (unmigrated) notification-service and
 * supplier-questionnaire-service still consume.
 */
export class CompanyLinkService {
  private readonly dataPoints: DataPoints;

  constructor(private readonly deps: CompanyLinkServiceDeps) {
    this.dataPoints = new DataPointsLib(console).initializeDataPoints();
  }

  async linkSingle(params: CompanyLinkSingleParams): Promise<void> {
    const {
      forCompanyId,
      byCompanyId,
      connectionType,
      auditData,
      notifyLink = true,
    } = params;

    const company = await getCompanyByIdDb(this.deps.db, forCompanyId);
    if (!company) {
      throw new NotFoundError({
        code: 'company_not_found',
        message: 'Company does not exist',
        details: { forCompanyId },
      });
    }

    const companyIds: CompanyIds = {
      forCompanyId,
      byCompanyId,
      onBehalfOfCompanyId: byCompanyId,
    };

    const profile = await this.performLink(
      companyIds,
      connectionType,
      auditData,
    );
    await this.linkReverse(companyIds, connectionType, auditData);

    if (profile) {
      await this.deps.publish(
        SUPPLIER_QUESTIONNAIRE_COMPANY_PROFILE_CREATE,
        profile as unknown as Record<string, unknown>,
      );
    }

    // Self-link: the DB writes above still happen, but there's no
    // notification/asset-share tail — matches legacy's early `return`.
    if (forCompanyId === byCompanyId) return;

    if (notifyLink) {
      await this.deps.publish(COMPANY_PROFILE.LINK, {
        companyId: byCompanyId,
        id: forCompanyId,
        connectionType,
        ...(profile ?? {}),
      });
    }

    await this.sharePublicAssets(companyIds);
  }

  async linkBulk(params: CompanyLinkBulkParams): Promise<void> {
    const { forCompanyIds, byCompanyId, connectionType, auditData } = params;

    for (const forCompanyId of forCompanyIds) {
      // Unlike linkSingle, bulk skips a self-link id entirely — no DB
      // writes, no publish, no asset-share — matching legacy `bulkLink`.
      if (forCompanyId === byCompanyId) continue;

      const company = await getCompanyByIdDb(this.deps.db, forCompanyId);
      if (!company) {
        throw new NotFoundError({
          code: 'company_not_found',
          message: 'Company does not exist',
          details: { forCompanyId },
        });
      }

      const companyIds: CompanyIds = {
        forCompanyId,
        byCompanyId,
        onBehalfOfCompanyId: byCompanyId,
      };

      const profile = await this.performLink(
        companyIds,
        connectionType,
        auditData,
      );
      await this.linkReverse(companyIds, connectionType, auditData);

      if (profile) {
        await this.deps.publish(COMPANY_PROFILE.LINK, {
          companyId: byCompanyId,
          id: forCompanyId,
          connectionType,
          ...profile,
        });
        await this.deps.publish(
          SUPPLIER_QUESTIONNAIRE_COMPANY_PROFILE_CREATE,
          profile as unknown as Record<string, unknown>,
        );
      }

      await this.sharePublicAssets(companyIds);
    }
  }

  /** Forward write: builds & persists companyIds' network profile of forCompanyId. */
  private async performLink(
    companyIds: CompanyIds,
    connectionType: string,
    auditData: AuditData,
  ): Promise<CompanyProfileRow | undefined> {
    const templateAnswers = await this.buildTemplateAnswers(companyIds);
    if (templateAnswers[ABOUT_TEMPLATE_ID]) {
      templateAnswers[ABOUT_TEMPLATE_ID].connection_type = {
        value: connectionType,
      };
    }

    await saveCompanyDataPointsAndTemplateAnswersDb(this.deps.db, {
      companyIds,
      templateAnswers,
      auditData,
    });

    return getCompanyAboutProfileDb(this.deps.db, companyIds);
  }

  /**
   * Reverse write: the other company's profile of the caller, using the
   * paired connectionType. Deliberately has NO existence check — unlike
   * `CompanyCreationService.linkOtherId`, this always rebuilds the reverse
   * profile, because the pair passed in is never null. Never throws — a
   * reverse-link failure must not fail the primary link request.
   */
  private async linkReverse(
    companyIds: CompanyIds,
    connectionType: string,
    auditData: AuditData,
  ): Promise<void> {
    try {
      if (companyIds.forCompanyId === companyIds.byCompanyId) return;

      const reverseIds: CompanyIds = {
        forCompanyId: companyIds.byCompanyId,
        byCompanyId: companyIds.forCompanyId,
        onBehalfOfCompanyId: companyIds.forCompanyId,
      };
      const pair = CONNECTION_TYPES_PAIR[connectionType];

      const templateAnswers = await this.buildTemplateAnswers(reverseIds);
      if (templateAnswers[ABOUT_TEMPLATE_ID]) {
        templateAnswers[ABOUT_TEMPLATE_ID].connection_type = { value: pair };
      }

      await saveCompanyDataPointsAndTemplateAnswersDb(this.deps.db, {
        companyIds: reverseIds,
        templateAnswers,
        auditData,
      });
    } catch {
      // Non-propagating — matches legacy `linkOtherId`.
    }
  }

  /**
   * Auto-shares companyIds.byCompanyId's public AUDITS assets with
   * companyIds.forCompanyId. Ports legacy `sharePublicAssets`, fixed to
   * filter on the asset's real top-level `isPublic` field (see
   * `listPublicAssetAnswersByCompanyIdDb`) instead of the legacy query's
   * `answer->'value'->>'is_public'` path, which never matched real rows.
   */
  private async sharePublicAssets(companyIds: CompanyIds): Promise<void> {
    const assets = await listPublicAssetAnswersByCompanyIdDb(
      this.deps.db,
      companyIds.byCompanyId,
    );
    if (assets.length === 0) return;

    const hasConnection = await hasAnyConnectionDb(this.deps.db, {
      forCompanyId: companyIds.forCompanyId,
      byCompanyId: companyIds.byCompanyId,
    });
    if (!hasConnection) {
      throw new NotFoundError({
        code: 'companies_connection_not_found',
        message: 'Companies have no connection established yet',
        details: companyIds,
      });
    }

    for (const row of assets) {
      await this.shareAsset(companyIds, row);
    }
  }

  private async shareAsset(
    companyIds: CompanyIds,
    row: DataPointAnswerRow,
  ): Promise<void> {
    const asset = row.answer as Record<string, unknown>;
    const assetId = row.assetId ?? (asset.id as string | undefined);
    const assetType = row.assetType;
    if (!assetId || !assetType) return;

    const ownedByCompanyId =
      (asset.uploaderCompanyId as string | undefined) ??
      (asset.companyId as string | undefined);
    const now = new Date();

    await upsertAssetAccessDb(this.deps.db, {
      assetType,
      assetId,
      ownedByCompanyId,
      sharedWithCompanyId: companyIds.forCompanyId,
      sharedByCompanyId: companyIds.byCompanyId,
      rights: RIGHTS_READ,
      status: ACCESS_STATUS_ACTIVE,
      createdAt: now,
      updatedAt: now,
    });

    const template = await getTemplateByIdDb(this.deps.db, assetType);
    if (!template) return;

    const templateAnswer = await createAssetDataPointAnswerWithTemplateDb(
      this.deps.db,
      {
        templateId: assetType,
        companyIds,
        templateDataPointKeys: template.composedOf,
        templateAnswer: asset,
        retracedDataPointId: row.retracedDataPointId,
      },
    );

    if (templateAnswer) {
      const value = templateAnswer.value as Record<string, unknown>;
      await upsertCompanyProfileDb(this.deps.db, {
        ...companyIds,
        profileType: assetType,
        data: value,
        ...(extractUserAuditMetaFromCtx(value) as Record<
          string,
          string | null
        >),
      });
    }
  }

  /**
   * Faithful port of legacy `buildTemplatesFromDataPointAnswers`: preserves
   * any answers already stored for the exact companyIds triple across every
   * template, then backfills/merges only the 3 core templates from
   * `forCompanyId`'s own defaults. On a brand-new pair (the common case) this
   * degenerates to "copy forCompanyId's core-template defaults" — but on a
   * re-link of an already-linked pair, previously-customized answers on
   * non-core templates are preserved rather than silently overwritten.
   */
  private async buildTemplateAnswers(
    companyIds: CompanyIds,
  ): Promise<TemplateAnswers> {
    const templates = await listTemplatesUnscopedDb(this.deps.db);
    const dataPointIds = templates.flatMap(t => t.composedOf);

    const existingAnswers = await getDataPointAnswersByDataPointsDb(
      this.deps.db,
      {
        ...companyIds,
        retracedDataPointIds: dataPointIds,
      },
    );

    const constructed = this.buildDataPointsAnswers(
      dataPointIds,
      existingAnswers,
    );
    const templateAnswers = this.pickTemplateComposedOf(constructed, templates);

    for (const coreTemplateId of COMPANY_CORE_INFO_TEMPLATES_ENUMS) {
      const current = templateAnswers[coreTemplateId];
      const template = templates.find(t => t.id === coreTemplateId);
      const defaults = await this.buildDefaultTemplateAnswers(
        companyIds.forCompanyId,
        template,
      );
      if (!defaults) continue;

      templateAnswers[coreTemplateId] = current
        ? this.mergeTemplateAnswers(current, defaults)
        : defaults;
    }

    return templateAnswers;
  }

  private buildDataPointsAnswers(
    dataPointIds: string[],
    answers: DataPointAnswerRow[],
  ): TemplateAnswer {
    const result: TemplateAnswer = {};
    for (const id of dataPointIds) {
      result[id] = { value: null };

      const row = answers.find(a => a.retracedDataPointId === id);
      if (!row?.answer) continue;

      const parsed = (
        typeof row.answer === 'string' ? JSON.parse(row.answer) : row.answer
      ) as DataPointValue;
      if (parsed?.value == null) continue;

      result[id] = parsed;
    }
    return result;
  }

  private pickTemplateComposedOf(
    constructed: TemplateAnswer,
    templates: Template[],
  ): TemplateAnswers {
    const templateAnswers: TemplateAnswers = {};
    for (const template of templates) {
      const picked: TemplateAnswer = {};
      for (const id of template.composedOf) {
        picked[id] = constructed[id];
      }
      const allNull = Object.values(picked).every(v => v?.value == null);
      if (allNull) continue;

      templateAnswers[template.id] = picked;
    }
    return templateAnswers;
  }

  /**
   * `main` and `secondary` are always built from the same template's
   * `composedOf`, so their key sets are identical — no need to guard a
   * missing key on either side.
   */
  private mergeTemplateAnswers(
    main: TemplateAnswer,
    secondary: TemplateAnswer,
  ): TemplateAnswer {
    const merged: TemplateAnswer = {};
    for (const key of Object.keys(main)) {
      merged[key] = main[key].value !== null ? main[key] : secondary[key];
    }
    return merged;
  }

  private async buildDefaultTemplateAnswers(
    companyId: string,
    template: Template | undefined,
  ): Promise<TemplateAnswer | null> {
    if (!template) return null;

    const rows = await getDataPointAnswersByDataPointsDb(this.deps.db, {
      forCompanyId: companyId,
      byCompanyId: companyId,
      onBehalfOfCompanyId: companyId,
      retracedDataPointIds: template.composedOf,
    });

    const filtered = rows.filter(row => {
      let dp: ReturnType<DataPoints['getDataPointById']> | undefined;
      try {
        dp = this.dataPoints.getDataPointById(row.retracedDataPointId);
      } catch {
        return false;
      }
      const type = dp?.dataPointDefinition?.type;
      return (
        type === 'GLOBAL' ||
        (type === 'RELATIONAL' &&
          RELATIONAL_DATA_POINTS_TO_COPY.includes(row.retracedDataPointId))
      );
    });

    return this.buildDataPointsAnswers(template.composedOf, filtered);
  }
}
